import React from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { ConditionToLabel } from '../../util/enums/CONDITION.js';

import ConditionBox from '../../components/ConditionBox/ConditionBox.js';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  Button,
} from '../../components';

import css from './ListingCard.module.css';
import LISTING_STATUS from '../../util/enums/LISTING_STATUS.js';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, {
  loadAfterInitialRendering: 3000,
});

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(
    conf => conf.listingType === listingType
  );
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(
    price,
    config.currency,
    intl
  );
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage
            id="ListingCard.perUnit"
            values={{ unitType: publicData?.unitType }}
          />
        </div>
      ) : null}
    </div>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const currentLocation = useLocation();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    onToggleFavorites,
    currentUser,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0
      ? currentListing.images[0]
      : null;
  // Pages that don't load stock (include: currentStock) show the listing as unsold
  // rather than crashing.
  const isSold = currentListing.currentStock?.attributes?.quantity === 0;
  const isReserved =
    currentListing.attributes?.publicData.listingStatus ===
    LISTING_STATUS.RESERVED;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k =>
        k.startsWith(variantPrefix)
      )
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  // Check if listing is favorited
  const favorites =
    currentUser?.attributes.profile.privateData?.favorites || [];
  const isFavorite = favorites.includes(currentListing.id.uuid);

  const toggleFavorites = e => {
    e.preventDefault();
    e.stopPropagation();
    if (onToggleFavorites) {
      onToggleFavorites(isFavorite);
    }
  };

  const favoriteButton =
    onToggleFavorites && currentUser ? (
      <Button
        type="button"
        rootClassName={isFavorite ? css.favoriteButtonActive : css.favoriteButton}
        onClick={toggleFavorites}
        title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        data-testid="favorite-button"
      >
        <svg
          width="20"
          height="20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
        >
          <path
            d="M12 20.84l-1.45-1.32C5.4 14.86 2 11.78 2 8 2 4.92 4.42 2.5 7.5 2.5c1.74 0 3.41 0.81 4.5 2.09C13.09 2.31 14.76 2.5 16.5 2.5 19.58 2.5 22 4.92 22 8c0 3.78-3.4 6.86-8.55 11.54L12 20.84z"
            fill={isFavorite ? 'white' : 'transparent'}
            stroke={isFavorite ? 'white' : '#1D1D1D'}
            strokeWidth="1.5"
          />
        </svg>
      </Button>
    ) : null;

  return (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
      to={{ state: { fromSearch: currentLocation.search, fromPathname: currentLocation.pathname } }}
    >
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
        {favoriteButton && (
          <div className={css.favoriteOverlay}>{favoriteButton}</div>
        )}
      </AspectRatioWrapper>
      <div className={css.info}>
        <div className={css.mainInfo}>
          <div className={css.titleWrapper}>
            <div className={css.title}>
              {richText(title, {
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
            </div>

            <div className={css.price}>
              {isReserved ? (
                <span className={css.priceValue}>RESERVED</span>
              ) : isSold ? (
                <span className={css.priceValue}>SOLD</span>
              ) : (
                <PriceMaybe
                  price={price}
                  publicData={publicData}
                  config={config}
                  intl={intl}
                />
              )}
            </div>
          </div>

          <div className={css.brandDesignerWrapper}>
            <span>
              {currentListing.attributes.publicData.brand} /{' '}
              {currentListing.attributes.publicData.designer}
            </span>
            <ConditionBox
              condition={ConditionToLabel(
                currentListing.attributes.publicData.condition
              )}
            />
          </div>

          {/* {showAuthorInfo ? (
            <div className={css.authorInfo}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
            </div>
          ) : null} */}
        </div>
      </div>
    </NamedLink>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
  onToggleFavorites: null,
  currentUser: null,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
  onToggleFavorites: func,
  currentUser: propTypes.currentUser,
};

export default injectIntl(ListingCardComponent);
